import { each, extend, get as lodashGet, omit, pick } from 'lodash';
import moment from 'moment';
import { axiosService, LoggerStatusEnum, sendLog } from 'ui-model';

import axiosClient from '@lib/utils/axiosClient';
import isOnEmbedPage from '@lib/utils/isOnEmbedPage';

import configService from './configService';

interface Storage {
  get: (key: string) => any;
  set: (key: string, value: any) => void;
  remove: (key: string) => void;
  clear: () => void;
}
interface StorageNative extends Omit<Storage, 'get'> {
  get: (key: string) => Promise<any>;
}

let storage: Storage | StorageNative;

function setStorage(storageClient: Storage | StorageNative) {
  storage = storageClient;
}

const appPrefix = configService.getAppPrefix();

// TODO: cleanup storage to make it more generic
export const KEYS = {
  token: `${appPrefix}:auth_token`,
  parameters: `${appPrefix}:parameters`,
  uploadedCards: `${appPrefix}:uploaded_cards`,
  patientFormPending: `${appPrefix}:patient_forms_pending`,
  user: `${appPrefix}:auth_user`,
  expirationTime: `${appPrefix}:expiration_time`,
  rwgToken: `${appPrefix}:persist:rwg_token`,
};

const PERSISTABLE_DATA: any = {};

function get(key: string) {
  try {
    return storage.get(key);
  } catch (e) {
    sendLog(
      'storageService - get',
      {
        key: key || 'no key',
        error: lodashGet(e, 'message', 'no - e message'),
      },
      LoggerStatusEnum.error,
    );
  }
}

function set(key: string, value: any) {
  if (key.includes('persist')) PERSISTABLE_DATA[key] = value;
  return storage.set(key, value);
}

function remove(key: string) {
  return storage.remove(key);
}

function clear() {
  return storage.clear();
}

function getLocaleKeyByUserId(userId: string) {
  return `${appPrefix}:locale:persist:${userId}`;
}

function getUser(): any {
  return get(KEYS.user);
}

function getSessionTimeMinutes() {
  // DISCUSSION should this go inside config ?
  return 30;
}

function resetSession() {
  const user = getUser();
  if (user) {
    if (user.type === 'doctor') {
      user.expireAt = moment().add(120, 'minutes');
      set(KEYS.user, user);
      return;
    }

    user.expireAt = moment().add(getSessionTimeMinutes(), 'minutes');
    set(KEYS.user, user);
  }
}

// FIXME remove dependencies on window
function startSessionRefresh() {
  // @ts-ignore
  window.addEventListener('click', resetSession);
  // @ts-ignore
  window.addEventListener('keydown', resetSession);
}

function stopSessionRefresh() {
  // @ts-ignore
  window.removeEventListener('click', resetSession);
  // @ts-ignore
  window.removeEventListener('keydown', resetSession);
}

function setUser(user: any) {
  const data = pick(user, ['token', 'expireAt', 'type']);
  set(KEYS.user, data);
  resetSession();
  startSessionRefresh();
}

function refreshSession() {
  const user = getUser();
  if (user) {
    setUser(user);
  }
}

function getToken() {
  const token = get(KEYS.token) || lodashGet(getUser(), 'token');
  return token;
}

function setToken(value: any) {
  // Do not set token on embeds
  if (isOnEmbedPage()) {
    return;
  }

  (window as any).token = value;

  return set(KEYS.token, value);
}

function setRwgToken(value: string) {
  return set(KEYS.rwgToken, value);
}

function getRwgToken() {
  return get(KEYS.rwgToken);
}

function getAllPersistableData() {
  const allLocalStorageKeys = Object.keys(localStorage);

  allLocalStorageKeys.forEach((key) => {
    if (key.includes('persist')) PERSISTABLE_DATA[key] = get(key);
  });
}

function setPersistableData() {
  each(PERSISTABLE_DATA, (value, key) => {
    if (value) storage.set(key, value);
  });
}

function removeAuth() {
  (window as any).token = undefined;
  stopSessionRefresh();

  getAllPersistableData();
  storage.clear();
  setPersistableData();

  // Remove auth data from axios instances
  axiosClient.defaults.headers.common = {
    ...axiosClient.defaults.headers.common,
    'x-access-token': '',
  };
  axiosService.defaults.headers.common = {
    ...axiosService.defaults.headers.common,
    'x-access-token': '',
  };
}

function getParameters() {
  return get(KEYS.parameters) || {};
}

function setParameters(value: any) {
  return set(KEYS.parameters, omit(value, ['token']));
}

function extendParameters(newParams: any) {
  const oldParams = getParameters();
  const params = extend({}, oldParams, newParams);
  setParameters(params);
  return params;
}

function getUploadedCards() {
  return get(KEYS.uploadedCards);
}

function setUploadedCards(value: any) {
  return set(KEYS.uploadedCards, value);
}

function getCurrentUserExpireAt() {
  return lodashGet(getUser(), 'expireAt');
}

function getPatientFormPending() {
  return get(KEYS.patientFormPending);
}

function setPatientFormPending(id: any) {
  return set(KEYS.patientFormPending, id);
}

function removePatientFormPending() {
  return remove(KEYS.patientFormPending);
}

export default {
  setStorage,
  get,
  set,
  remove,
  clear,
  getToken,
  setToken,
  removeAuth,
  getParameters,
  setParameters,
  getUploadedCards,
  setUploadedCards,
  extendParameters,
  getLocaleKeyByUserId,
  getUser,
  setUser,
  getCurrentUserExpireAt,
  getPatientFormPending,
  setPatientFormPending,
  removePatientFormPending,
  getSessionTimeMinutes,
  refreshSession,
  setRwgToken,
  getRwgToken,
};
